import React, { useEffect, useState } from "react"
import ApexCharts from "react-apexcharts"
import useFetchTemplates from "../../hooks/useFetchTemplates"
import {
	convertMinutesToMilitaryTime,
	timeStringToMinutes,
	getDayOfWeek
} from "../../utils/generalUtils"
import { PageLoader } from "../Authentication/page-loader"

const Barchart = ({
	analyze,
	templatesData: providedTemplatesData,
	showCancellations = true,
	showAddOns = true,
	viewDetailsAction = null,
	compact = false
}) => {
	const [chartData, setChartData] = useState([])

	// Allow for direct template data injection or use the hook
	const fetchedTemplates = useFetchTemplates()
	const templatesData = providedTemplatesData || fetchedTemplates.templatesData
	const templatesStatus = providedTemplatesData ? "success" : fetchedTemplates.templatesStatus
	const templatesError = providedTemplatesData ? null : fetchedTemplates.templatesError

	useEffect(() => {
		setChartData(analyze.utilization)
	}, [analyze])

	if (!templatesData || templatesStatus === "loading" || templatesError) {
		return <PageLoader />
	}

	// 1. Determine the day of the week from analyze.date
	const dayOfWeek = getDayOfWeek(analyze.date)

	// 2. Find the template active on this date for that weekday. Multiple
	// versions share a day_of_week (each with a startDate/endDate validity
	// window), so pick the one covering analyze.date rather than the first
	// match — otherwise an expired template's capacity line gets plotted.
	const analyzeDate = new Date(analyze.date + "T12:00:00")
	const weekdayTemplates = templatesData.filter(temp => temp.day_of_week === dayOfWeek)
	const template =
		weekdayTemplates.find(temp => {
			const start = temp.startDate ? new Date(temp.startDate) : null
			const end = temp.endDate ? new Date(temp.endDate) : null
			return (!start || start <= analyzeDate) && (!end || end > analyzeDate)
		}) || weekdayTemplates[0]

	// 3. Extract expectedPatients data from the template
	let templateDataMap = {}
	let maxTemplate = 0
	if (template && template.schedule) {
		template.schedule.forEach(item => {
			templateDataMap[timeStringToMinutes(item.time)] = item.expectedPatients
		})
		maxTemplate = Math.max(...template.schedule.map(item => item.expectedPatients))
	}

	// 4. Align the template data with the chart's time categories
	const timeCategories = chartData.map(item => convertMinutesToMilitaryTime(item.time))

	const maxScheduled = Math.max(...chartData.map(item => item.scheduled))
	const maxScheduledWithAddOns = Math.max(
		...chartData.map(item => item.scheduled + (showAddOns ? item.addOns || 0 : 0))
	)
	const maxValue = Math.max(showAddOns ? maxScheduledWithAddOns : maxScheduled, maxTemplate)

	// Build series array dynamically based on what should be shown
	const seriesData = []

	// Base scheduled appointments always shown
	seriesData.push({
		name: "Scheduled Appointments",
		type: "column",
		data: chartData.map(item => item.scheduled)
	})

	// Optional anticipated cancellations
	if (showCancellations) {
		seriesData.push({
			name: "Anticipated Cancellations",
			type: "column",
			data: chartData.map(item => Math.round(item.noShows || 0))
		})
	}

	// Optional anticipated add-ons
	if (showAddOns) {
		seriesData.push({
			name: "Anticipated Add-ons",
			type: "column",
			data: chartData.map(item => Math.round(item.addOns || 0))
		})
	}

	// Target patients line always shown
	seriesData.push({
		name: "Target Patients",
		type: "line",
		data: chartData.map(item => {
			const timeKey = item.time
			return templateDataMap[timeKey] || 0
		})
	})

	// Define appropriate colors based on what's shown
	const colors = []
	colors.push("#6955EA") // Scheduled
	if (showCancellations) colors.push("#352D66") // Cancellations
	if (showAddOns) colors.push("#AEA2FF") // Add-ons
	colors.push("#720b32") // Target

	// Calculate proper stroke and dash array configs
	const strokeWidths = [0] // Start with scheduled (column)
	if (showCancellations) strokeWidths.push(0) // Cancellations (column)
	if (showAddOns) strokeWidths.push(0) // Add-ons (column)
	strokeWidths.push(3) // Target (line)

	const dashArrays = [0] // Start with scheduled (solid)
	if (showCancellations) dashArrays.push(0) // Cancellations (solid)
	if (showAddOns) dashArrays.push(0) // Add-ons (solid)
	dashArrays.push(0) // Target (solid)

	const options = {
		chart: {
			type: "line",
			height: 350,
			stacked: true,
			toolbar: {
				show: false
			},
			fontFamily: "Manrope, sans-serif",
			background: "transparent",
			animations: {
				enabled: true,
				easing: "easeinout",
				speed: 800,
				animateGradually: {
					enabled: true,
					delay: 150
				},
				dynamicAnimation: {
					enabled: true,
					speed: 350
				}
			}
		},
		plotOptions: {
			bar: {
				horizontal: false,
				columnWidth: compact ? "40%" : "50%",
				borderRadius: 4,
				borderRadiusApplication: "end",
				borderRadiusWhenStacked: "last",
				distributed: false,
				dataLabels: {
					position: "top"
				}
			}
		},
		stroke: {
			width: strokeWidths,
			curve: "smooth",
			dashArray: dashArrays
		},
		xaxis: {
			categories: timeCategories,
			labels: {
				style: {
					fontFamily: "Manrope, sans-serif",
					fontSize: compact ? "10px" : "12px",
					colors: "#4B5563"
				},
				rotate: 0
			},
			axisBorder: {
				show: false
			},
			axisTicks: {
				show: false
			},
			crosshairs: {
				show: true,
				position: "back",
				stroke: {
					color: "#b6b6b6",
					width: 1,
					dashArray: 3
				}
			}
		},
		yaxis: {
			title: {
				text: "Patients",
				style: {
					fontFamily: "Manrope, sans-serif",
					fontSize: "13px",
					fontWeight: 500,
					color: "#4B5563"
				}
			},
			max: Math.max(analyze.numberOfChairs, maxValue + 2),
			min: 0,
			labels: {
				style: {
					colors: "#4B5563",
					fontSize: compact ? "10px" : "12px"
				},
				formatter: function (val) {
					// In compact mode, only show whole numbers
					return compact ? Math.round(val) : val
				}
			}
		},
		colors: colors,
		dataLabels: {
			enabled: !compact,
			formatter: function (val, { seriesIndex, dataPointIndex, w }) {
				// Only show labels for scheduled appointments
				if (seriesIndex === 0 && val > 0) {
					return val
				}
				return ""
			},
			style: {
				fontSize: "12px",
				fontWeight: "bold",
				colors: ["#4B5563"]
			},
			background: {
				enabled: false
			},
			offsetY: -20
		},
		grid: {
			borderColor: "#F3F4F6",
			strokeDashArray: 4,
			xaxis: {
				lines: {
					show: true
				}
			},
			yaxis: {
				lines: {
					show: true
				}
			},
			padding: {
				top: 0,
				right: compact ? 10 : 20,
				bottom: 0,
				left: compact ? 5 : 10
			}
		},
		tooltip: {
			shared: true,
			intersect: false,
			theme: "light",
			y: {
				formatter: function (val, { seriesIndex, dataPointIndex, w }) {
					return val + " patients"
				}
			},
			x: {
				formatter: function (val, { dataPointIndex }) {
					// Use the formatted time from timeCategories instead of the raw numerical value
					return `Time: ${timeCategories[dataPointIndex] || val}`
				}
			},
			marker: {
				show: true
			}
		},
		legend: {
			show: true,
			position: "top",
			horizontalAlign: "right",
			fontFamily: "Manrope, sans-serif",
			fontSize: "13px",
			markers: {
				width: 12,
				height: 12,
				radius: 6,
				offsetX: 0,
				offsetY: 0
			},
			itemMargin: {
				horizontal: 12,
				vertical: 5
			},
			labels: {
				colors: "#4B5563",
				useSeriesColors: false
			}
		},
		fill: {
			opacity: 1,
			type: "solid"
		},
		states: {
			hover: {
				filter: {
					type: "darken",
					value: 0.1
				}
			},
			active: {
				filter: {
					type: "none"
				}
			}
		},
		responsive: [
			{
				breakpoint: 640,
				options: {
					chart: {
						height: 300
					},
					legend: {
						position: "bottom",
						horizontalAlign: "center"
					}
				}
			}
		]
	}

	// For compact view (used in FutureHuddleDayOverview)
	if (compact) {
		return (
			<div className="w-full">
				<ApexCharts options={options} series={seriesData} type="line" height={200} />
			</div>
		)
	}

	// Full view with header and summary stats
	return (
		<div className="w-full p-4 space-y-3">
			{/* Chart */}
			<div className="bg-white rounded-lg p-3 border border-gray-100 shadow-sm">
				<ApexCharts options={options} series={seriesData} type="line" height={350} />
			</div>
		</div>
	)
}

export default Barchart
